/**
 * User Activity API
 *
 * Create events (fire-and-forget from the tracker) and list recent events
 * for the Sales Manager admin page.
 */

import { dataverseClient, TABLES, type QueryOptions } from '@shared/api';
import { assertDateLiteral } from '@shared/lib';
import type {
  UserActivity,
  CreateUserActivityInput,
  UserActivitySummary,
} from '../model/types';

interface UserActivityEntity {
  vba_useractivityid: string;
  vba_name?: string;
  vba_userobjectid?: string;
  vba_username?: string;
  vba_useremail?: string;
  vba_app?: string;
  vba_eventtype?: string;
  vba_area?: string;
  vba_path?: string;
  vba_sessionid?: string;
  vba_referrerpath?: string;
  vba_queryparams?: string;
  vba_durationms?: number;
  vba_context?: string;
  createdon?: string;
  [key: string]: unknown;
}

const SELECT_FIELDS = [
  'vba_useractivityid',
  'vba_name',
  'vba_userobjectid',
  'vba_username',
  'vba_useremail',
  'vba_app',
  'vba_eventtype',
  'vba_area',
  'vba_path',
  'vba_sessionid',
  'vba_referrerpath',
  'vba_queryparams',
  'vba_durationms',
  'vba_context',
  'createdon',
];

function mapToDomain(entity: UserActivityEntity): UserActivity {
  return {
    id: entity.vba_useractivityid,
    name: entity.vba_name ?? '',
    userObjectId: entity.vba_userobjectid ?? '',
    userName: entity.vba_username ?? '',
    userEmail: entity.vba_useremail ?? '',
    app: entity.vba_app ?? '',
    eventType: entity.vba_eventtype ?? '',
    area: entity.vba_area ?? undefined,
    path: entity.vba_path ?? undefined,
    sessionId: entity.vba_sessionid ?? undefined,
    referrerPath: entity.vba_referrerpath ?? undefined,
    queryParams: entity.vba_queryparams ?? undefined,
    durationMs: entity.vba_durationms ?? undefined,
    context: entity.vba_context ?? undefined,
    createdOn: entity.createdon ?? '',
  };
}

function truncate(value: string | undefined, max: number): string | undefined {
  if (value === undefined || value === null) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

function buildName(input: CreateUserActivityInput): string {
  const who = input.userEmail || input.userName || input.userObjectId;
  const what = input.eventType;
  const where = input.area ? ` · ${input.area}` : '';
  const when = new Date().toISOString();
  return truncate(`${what} · ${who}${where} · ${when}`, 400) ?? '';
}

async function create(input: CreateUserActivityInput): Promise<void> {
  const payload: Record<string, unknown> = {
    vba_name: buildName(input),
    vba_userobjectid: truncate(input.userObjectId, 50),
    vba_username: truncate(input.userName, 200),
    vba_useremail: truncate(input.userEmail, 200),
    vba_app: truncate(input.app, 20),
    vba_eventtype: truncate(input.eventType, 30),
  };
  if (input.area) payload.vba_area = truncate(input.area, 100);
  if (input.path) payload.vba_path = truncate(input.path, 500);
  if (input.sessionId) payload.vba_sessionid = truncate(input.sessionId, 50);
  if (input.referrerPath) payload.vba_referrerpath = truncate(input.referrerPath, 500);
  if (input.queryParams) payload.vba_queryparams = truncate(input.queryParams, 500);
  if (typeof input.durationMs === 'number') {
    payload.vba_durationms = Math.max(0, Math.min(86_400_000, Math.round(input.durationMs)));
  }
  if (input.context) {
    try {
      payload.vba_context = truncate(JSON.stringify(input.context), 4000);
    } catch {
      /* non-serializable, skip */
    }
  }

  await dataverseClient.create<UserActivityEntity>(TABLES.USER_ACTIVITY, payload, {
    returnRepresentation: false,
  });
}

/** List recent events, optionally filtered to a window (since, until). */
async function listRecent(
  sinceIso?: string,
  untilIso?: string,
  top = 2000,
): Promise<UserActivity[]> {
  const options: QueryOptions = {
    select: SELECT_FIELDS,
    orderBy: 'createdon desc',
    top,
  };
  const filters: string[] = [];
  if (sinceIso) filters.push(`createdon ge ${assertDateLiteral(sinceIso, 'sinceIso')}`);
  if (untilIso) filters.push(`createdon le ${assertDateLiteral(untilIso, 'untilIso')}`);
  if (filters.length) options.filter = filters.join(' and ');
  const response = await dataverseClient.list<UserActivityEntity>(
    TABLES.USER_ACTIVITY,
    options,
  );
  return response.value.map(mapToDomain);
}

/**
 * Build a per-user summary from a flat list of events.
 * Client-side roll-up keeps the server query simple.
 *
 * Login count is derived from distinct sessionIds rather than 'login' events,
 * because a single auth-time login is tagged with whichever app's URL the user
 * happened to land on (often vera-shell), so per-app filters would otherwise
 * miss the login. A "login" here means "this user had ≥1 event in a distinct
 * browser session within the filtered set."
 */
function summarize(events: UserActivity[]): UserActivitySummary[] {
  const byUser = new Map<string, UserActivitySummary>();
  const sessionRangesByUser = new Map<
    string,
    Map<string, { firstAt: string; lastAt: string }>
  >();
  for (const e of events) {
    const key = e.userObjectId || e.userEmail || e.userName;
    if (!key) continue;
    let agg = byUser.get(key);
    if (!agg) {
      agg = {
        userObjectId: e.userObjectId,
        userName: e.userName,
        userEmail: e.userEmail,
        loginCount: 0,
        lastLoginAt: undefined,
        lastSeenAt: e.createdOn,
        areasVisited: [],
        totalEvents: 0,
      };
      byUser.set(key, agg);
      sessionRangesByUser.set(key, new Map());
    }
    agg.totalEvents += 1;
    if (e.createdOn > agg.lastSeenAt) agg.lastSeenAt = e.createdOn;
    // Keep the most recent name/email for display
    if (e.createdOn === agg.lastSeenAt) {
      if (e.userName) agg.userName = e.userName;
      if (e.userEmail) agg.userEmail = e.userEmail;
    }
    if (e.sessionId) {
      const ranges = sessionRangesByUser.get(key)!;
      const range = ranges.get(e.sessionId);
      if (!range) {
        ranges.set(e.sessionId, { firstAt: e.createdOn, lastAt: e.createdOn });
      } else {
        if (e.createdOn < range.firstAt) range.firstAt = e.createdOn;
        if (e.createdOn > range.lastAt) range.lastAt = e.createdOn;
      }
    }
    if ((e.eventType === 'area_visit' || e.eventType === 'page_view') && e.area) {
      const existing = agg.areasVisited.find((a) => a.area === e.area);
      if (existing) {
        existing.count += 1;
        if (e.createdOn > existing.lastAt) existing.lastAt = e.createdOn;
      } else {
        agg.areasVisited.push({ area: e.area, count: 1, lastAt: e.createdOn });
      }
    }
  }
  for (const [key, agg] of byUser.entries()) {
    agg.areasVisited.sort((a, b) => b.count - a.count);
    const ranges = sessionRangesByUser.get(key);
    if (ranges && ranges.size > 0) {
      agg.loginCount = ranges.size;
      let mostRecent: { firstAt: string; lastAt: string } | null = null;
      for (const range of ranges.values()) {
        if (!mostRecent || range.lastAt > mostRecent.lastAt) {
          mostRecent = range;
        }
      }
      if (mostRecent) agg.lastLoginAt = mostRecent.firstAt;
    }
  }
  return Array.from(byUser.values()).sort((a, b) =>
    a.lastSeenAt < b.lastSeenAt ? 1 : a.lastSeenAt > b.lastSeenAt ? -1 : 0,
  );
}

export const userActivityApi = {
  create,
  listRecent,
  summarize,
};
